import { PricingPlan } from './types';

export interface PricingPlanIdentity {
  code?: string | null;
  legacy?: boolean | null;
}

export interface AccountPricingPlanReference {
  pricing_plan?: string | null;
  pricing_plan_legacy?: boolean | null;
  pricing_plan_metadata?: PricingPlanIdentity;
}

/**
 * Produces a stable UI identity for a pricing plan. Display names are
 * intentionally excluded because more than one plan may share one.
 */
export const getPricingPlanIdentity = ({ code, legacy }: PricingPlanIdentity): string => {
  const legacyState = legacy === true ? 'legacy' : legacy === false ? 'current' : 'unknown';
  return `${code ?? 'all'}:${legacyState}`;
};

/** A plan can only be resolved when the API supplied both parts of its identity. */
export const hasPricingPlanIdentity = (
  pricingPlan?: PricingPlanIdentity
): pricingPlan is { code: string; legacy: boolean } =>
  typeof pricingPlan?.code === 'string' && typeof pricingPlan?.legacy === 'boolean';

/**
 * Resolves an account's current catalog plan. The name + Backend legacy lookup is
 * a temporary bridge until account responses expose canonical plan identity.
 */
export const resolveAccountPricingPlan = (
  account?: AccountPricingPlanReference,
  pricingPlans?: PricingPlan[] | null
): PricingPlan | undefined => {
  if (!pricingPlans?.length) return undefined;

  if (hasPricingPlanIdentity(account?.pricing_plan_metadata)) {
    const matchingPlans = pricingPlans.filter(
      (plan) =>
        getPricingPlanIdentity(plan) === getPricingPlanIdentity(account.pricing_plan_metadata)
    );
    return matchingPlans.length === 1 ? matchingPlans[0] : undefined;
  }

  if (typeof account?.pricing_plan !== 'string') return undefined;

  // While PRO_PLAN_ENABLED is off, the Backend omits `pricing_plan_legacy` and
  // never lists the new non-legacy plan sharing that name, so a name-only match
  // is unambiguous. Only fall back to it when legacy metadata is truly absent.
  const matchingPlans =
    typeof account.pricing_plan_legacy === 'boolean'
      ? pricingPlans.filter(
          (plan) =>
            plan.name === account.pricing_plan && plan.legacy === account.pricing_plan_legacy
        )
      : pricingPlans.filter((plan) => plan.name === account.pricing_plan);

  return matchingPlans.length === 1 ? matchingPlans[0] : undefined;
};

/** Legacy is opt-in metadata; never infer it from a plan name or code. */
export const isLegacyPricingPlan = ({ legacy }: PricingPlanIdentity): boolean => legacy === true;
